import { AbstractControl, UntypedFormGroup, NgControl } from '@angular/forms';
import { CmgUiUtilities } from '../../cmg-1003/components/common/cmg-ui-utils';
import { InvalidControlModel } from '../../cmg-1003/models/validation-panel.model';

/**
 * Finds all the controls in a FormGroup that are invalid. Returns them as InvalidControlModel[].
 * Marks form as dirty and touched.
 * @param form The FormGroup to validate.
 * @param assembleErrorMessage A callback function that determines the validation message for a FormControl.
 * @param keysRequiringError Any keys (i.e. FormControl names) that should be treated as an error, instead of a warning.
 */
export const findInvalidControls = (form: UntypedFormGroup, assembleErrorMessage: (key: string, invalidControl: InvalidControlModel) => void) => {
  CmgUiUtilities.markFormAsDirty(form);
  CmgUiUtilities.markFormAsTouched(form);
  let invalidControls: InvalidControlModel[] = [];

  if (form.invalid) {
    const controlsWithErrors = CmgUiUtilities.getControlsWithErrors(form);
    invalidControls = CmgUiUtilities.getInvalidControlModels(controlsWithErrors);

    invalidControls.forEach((t) => {
      if (t && t.errors) {
        assembleErrorMessage(t.formControlName, t);
      }
    });
  }

  return invalidControls;
};

/**
 * Form Controls Required to be populated before Save Loan can be executed
 */
export const requiredSaveFieldsUrla = [
  'retailLoCompPlan',
  'retailLeadType',
  'leadTypeReason',
  'referralPartner',
  'prepaymentPenaltyTerm',
  'propertyStreet',
  'propertyState',
  'infoProvidedVia',
  'isHispanicOrLatino',
  'isFemale',
  'isAmericanIndianOrAlaskaNative',
  'helocMaxBalance',
  'minimumDrawAmount',
  'loCompType',
  'brokerCompDisclosureType',
  'borrowerPaidCompPercent',
  'borrowerPaidCompAmount',
  'loCompForLenderPercent',
  'loCompForLenderAmount',
  'ethnicityCompleted',
  'genderCompleted',
  'raceCompleted',
];

export const requiredSaveFieldsLoanSummary = [
  'first',
  'street',
  'last',
  'zipCode',
  'years',
  'months',
  'city',
  'streetforUI',
  'state',
  'dob',
  'ssn',
  'livingStatus',
  'maritalStatus',
  'citizenship',
  'retailLoCompPlan',
  'retailLeadType',
  'leadTypeReason',
];

/**
 * Checks if controlId passed in is one of the fields required for Save Loan
 * @param controlId Id of the control to check
 * @returns True if controlId is one of the required fields
 */
export const isRequiredSaveField = (controlId: string): boolean => {
  let isRequired = false;
  requiredSaveFieldsUrla.forEach((f) => {
    if (controlId?.includes(f)) {
      isRequired = true;
    }
  });
  return isRequired;
};

export const isRequiredSaveFieldLoanSummary = (controlId: string): boolean => {
  let isRequired = false;
  requiredSaveFieldsLoanSummary.forEach((f) => {
    if (controlId.includes(f)) {
      isRequired = true;
    }
  });
  return isRequired;
};

export const showValidationFailure = (control: AbstractControl | NgControl) => control.invalid && (control.dirty || control.touched);

export const resetValidations = (form: UntypedFormGroup) => {
  CmgUiUtilities.markFormAsDirty(form, false, false);
  CmgUiUtilities.markFormAsTouched(form, false, false);
};

/** Finds the unique InvalidControl objects, based on the invalidMessage value. */
export const findUniqueByMessage = (invalidControls: InvalidControlModel[]) => {
  const uniqueInvalidControls = [];

  invalidControls?.forEach((control) => {
    if (uniqueInvalidControls.map((uniqueControl) => uniqueControl.invalidMessage).indexOf(control.invalidMessage) < 0) {
      uniqueInvalidControls.push(control);
    }
  });

  return uniqueInvalidControls;
};

/**
 *  Finds the unique InvalidControl objects, based on the controlId value.
 *  This is needed b/c messages now contain borrower name. As the user changes borrower name,
 *  the message becomes different for the same validation error on a given control and
 *  it shows up twice.
 *  This brings up another issue, what if there are multiple validation errors on a given
 *  control - right now, I haven't come across this situation. Also, we may need to
 *  redesign the structure of validation object in state - it lends itself to too many updates.
 */
export const findUniqueByControlId = (invalidControls: InvalidControlModel[]) => {
  const uniqueInvalidControls = [];

  invalidControls?.forEach((control) => {
    if (uniqueInvalidControls.map((uniqueControl) => uniqueControl.controlId).indexOf(control.controlId) < 0) {
      uniqueInvalidControls.push(control);
    }
  });

  return uniqueInvalidControls;
};

export const getInvalidControlModel = (control: AbstractControl, message?: string): InvalidControlModel => {
  const invalidControl: InvalidControlModel = {
    controlId: (<any>control).nativeElement?.id,
    invalidMessage: message,
  };
  return invalidControl;
};
